/**
 * buildInfo.ts — what commit this bundle was built from (BL-094).
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠ THE `"undefined"` TRAP, WHICH IS THE ENTIRE REASON THIS FILE IS NOT A ONE-LINER
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * The obvious implementation is `const sha = import.meta.env.VITE_COMMIT_SHA || 'unknown'`. It is
 * wrong on this fleet. When a `VITE_*` var is UNSET, Vite inlines the literal text `undefined`
 * into the bundle — a seven-character non-empty string, therefore TRUTHY, therefore `||` never
 * fires and the app confidently reports its commit as `undefined`. That mechanism took down
 * ptc-statetax with AADSTS900023 (an environment id of `"undefined"` reached Azure AD).
 *
 * So the values here come from vite `define`, which substitutes JSON — a missing commit arrives as
 * the JS literal `null`, which cannot be mistaken for a value — and every read still goes through
 * `normalizeCommit()`, which rejects `"undefined"`, `"null"` and the all-zero SHA by name.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THE `typeof` GUARDS
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `define` performs a TEXTUAL substitution of the identifier. Under vitest the defines are absent
 * (vitest.config.ts deliberately does not carry them, so the unknown path is the one the suite
 * exercises), and a bare reference to an undeclared identifier is a ReferenceError. `typeof` on an
 * undeclared identifier is the one form that is legal, and after substitution it constant-folds.
 */

/** The single token meaning "we do not know". Mirrors `UNKNOWN` in scripts/build-stamp.mjs. */
export const UNKNOWN = 'unknown';

/**
 * Strings a naive truthiness check would wave through as a real commit. `undefined`/`null` are the
 * bundler-inlined texts; the zero SHA is the shape of an answer without being one.
 */
const NOT_A_COMMIT = new Set([
  '',
  'undefined',
  'null',
  'unknown',
  'none',
  'head',
  '0000000000000000000000000000000000000000',
]);

/** Coerces anything to a real commit SHA or null. Exported because it is the guard worth testing. */
export function normalizeCommit(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (NOT_A_COMMIT.has(v)) return null;
  return /^[0-9a-f]{7,40}$/.test(v) ? v : null;
}

/** First 7 hex chars, or the `unknown` token — never a truncated placeholder. */
export function shortCommit(value: unknown): string {
  const c = normalizeCommit(value);
  return c === null ? UNKNOWN : c.slice(0, 7);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * The tagged one-line literal `scripts/build-stamp.mjs` defines and `verify-deploy.mjs` greps out
 * of the deployed bundle. It must remain a plain string constant that something in the app
 * REFERENCES — `define` only substitutes identifiers that actually appear, and a value nothing
 * reads is dropped by tree-shaking. `BuildInfoLine` (Settings) and the `window` handle below are
 * that reference; do not remove both.
 */
export const BUILD_STAMP: string =
  typeof __BUILD_STAMP__ === 'string' ? __BUILD_STAMP__ : `ptc-supervision-build v1 commit=${UNKNOWN} time=${UNKNOWN} dirty=${UNKNOWN}`;

export const BUILD_COMMIT: string | null =
  typeof __BUILD_COMMIT__ === 'string' ? normalizeCommit(__BUILD_COMMIT__) : null;

export const BUILD_COMMIT_TIME: string | null =
  typeof __BUILD_COMMIT_TIME__ === 'string' ? readString(__BUILD_COMMIT_TIME__) : null;

/** Tri-state: `true` dirty, `false` clean, `null` unknown. `false !== null` here and it matters. */
export const BUILD_DIRTY: boolean | null =
  typeof __BUILD_DIRTY__ === 'boolean' ? __BUILD_DIRTY__ : null;

export interface BuildInfo {
  commit: string | null;
  commitShort: string;
  commitTime: string | null;
  dirty: boolean | null;
  stamp: string;
}

export const buildInfo: BuildInfo = {
  commit: BUILD_COMMIT,
  commitShort: shortCommit(BUILD_COMMIT),
  commitTime: BUILD_COMMIT_TIME,
  dirty: BUILD_DIRTY,
  stamp: BUILD_STAMP,
};

/**
 * Human line for the UI and for a console paste. Always says something true: `unknown` when the
 * build carried no git information, and a loud marker when the bundle was built from a dirty tree
 * (in which case the SHA names the commit, not the code).
 */
export function buildLabel(info: BuildInfo = buildInfo): string {
  const parts = [info.commitShort];
  if (info.commitTime) {
    const t = new Date(info.commitTime);
    parts.push(Number.isNaN(t.getTime()) ? info.commitTime : t.toISOString().slice(0, 16).replace('T', ' ') + ' UTC');
  }
  if (info.dirty === true) parts.push('modified tree');
  return parts.join(' · ');
}

/**
 * Publishes the stamp on `window` so it can be read from a browser console on production without
 * a build or a deploy record — one of the four things BL-094 says cost real time this week.
 */
export function installBuildStampGlobal(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, unknown>).__PTC_BUILD__ = buildInfo;
}
